interface PixelCrop {
  x: number;
  y: number;
  width: number;
  height: number;
}

export const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous'); // needed to avoid cross-origin issues
    image.src = url;
  });

export async function getCroppedImg(
  imageSrc: string,
  pixelCrop?: PixelCrop,
  rotation: number = 0
): Promise<string> {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('No 2d context');
  }

  const normalizedRotation = ((rotation % 360) + 360) % 360;
  const rotRad = (normalizedRotation * Math.PI) / 180;
  const is90or270 = normalizedRotation === 90 || normalizedRotation === 270;

  // Calculate bounding box of the rotated image
  const rotWidth = is90or270 ? image.naturalHeight : image.naturalWidth;
  const rotHeight = is90or270 ? image.naturalWidth : image.naturalHeight;

  // Create an offscreen canvas for rotation
  const rotCanvas = document.createElement('canvas');
  rotCanvas.width = rotWidth;
  rotCanvas.height = rotHeight;
  const rotCtx = rotCanvas.getContext('2d');
  if (!rotCtx) {
    throw new Error('No 2d context for rotation');
  }

  rotCtx.translate(rotWidth / 2, rotHeight / 2);
  rotCtx.rotate(rotRad);
  rotCtx.drawImage(image, -image.naturalWidth / 2, -image.naturalHeight / 2);

  const crop = pixelCrop && pixelCrop.width > 0 && pixelCrop.height > 0
    ? pixelCrop
    : { x: 0, y: 0, width: rotWidth, height: rotHeight };

  // Limit output dimensions to max 512px to ensure base64 stays lightweight, sharp, and instant
  const maxDim = 512;
  let targetWidth = crop.width;
  let targetHeight = crop.height;

  if (targetWidth > maxDim || targetHeight > maxDim) {
    if (targetWidth > targetHeight) {
      targetHeight = (maxDim / targetWidth) * targetHeight;
      targetWidth = maxDim;
    } else {
      targetWidth = (maxDim / targetHeight) * targetWidth;
      targetHeight = maxDim;
    }
  }

  canvas.width = Math.max(1, Math.round(targetWidth));
  canvas.height = Math.max(1, Math.round(targetHeight));

  ctx.drawImage(
    rotCanvas,
    Math.max(0, crop.x),
    Math.max(0, crop.y),
    Math.min(rotWidth, crop.width),
    Math.min(rotHeight, crop.height),
    0,
    0,
    canvas.width,
    canvas.height
  );

  // Use 0.65 quality for crystal clarity and ultra-fast base64 upload (<50KB)
  return canvas.toDataURL('image/jpeg', 0.65);
}
